// eslint-disable-next-line import/no-extraneous-dependencies
import html2canvas from 'html2canvas';
// eslint-disable-next-line import/no-extraneous-dependencies
import JsPDF from 'jspdf';
import dayjs from 'dayjs';

interface GeneratePDFOptions {
  element: HTMLElement;
  fileName: string;
  onProgress?: (message: string) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export default async function generatePDF(options: GeneratePDFOptions): Promise<void> {
  const {
    element,
    fileName,
    onProgress,
    onSuccess,
    onError,
  } = options;

  try {
    onProgress?.('Generando PDF...');

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const { scrollHeight, scrollWidth } = element;

    const canvas = await html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: scrollWidth,
      height: scrollHeight,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      removeContainer: false,
      foreignObjectRendering: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector('[data-ref="report-container"]') || clonedDoc.body;
        if (clonedElement) {
          (clonedElement as HTMLElement).style.width = `${scrollWidth}px`;
          (clonedElement as HTMLElement).style.height = `${scrollHeight}px`;
          (clonedElement as HTMLElement).style.overflow = 'visible';
        }
      },
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const logoUrl = '/img/new_logo_cdtec_blue.png';
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const logoWidth = pdfWidth * 0.25;
    const logoHeight = 15;
    const logoX = 10;
    const logoY = 10;

    const logoImg = await new Promise<string>((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const logoCanvas = document.createElement('canvas');
        logoCanvas.width = img.width;
        logoCanvas.height = img.height;
        const ctx = logoCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(logoCanvas.toDataURL('image/png'));
        } else {
          reject(new Error('Canvas context not available'));
        }
      };
      img.onerror = reject;
      img.src = logoUrl;
    });
    pdf.addImage(logoImg, 'PNG', logoX, logoY, logoWidth, logoHeight);

    const titleLine1 = 'INFORME GESTIÓN Y OPERACIÓN';
    const titleLine2 = 'DE RIEGO';
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(128, 128, 128);
    const titleX = logoX + logoWidth + 10;
    const titleY = logoY + 5;

    pdf.text(titleLine1, titleX, titleY);
    pdf.text(titleLine2, titleX, titleY + 8);

    // Línea horizontal de separación
    const separatorY = titleY + 15;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(10, separatorY, pdfWidth - 10, separatorY);

    const contentY = separatorY + 10;
    const imgWidth = pdfWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    if (imgHeight > pdf.internal.pageSize.getHeight() - contentY) {
      const scale = (pdf.internal.pageSize.getHeight() - contentY) / imgHeight;
      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;
      const xOffset = (pdfWidth - scaledWidth) / 2;
      pdf.addImage(imgData, 'PNG', xOffset, contentY, scaledWidth, scaledHeight);
    } else {
      const xOffset = (pdfWidth - imgWidth) / 2;
      pdf.addImage(imgData, 'PNG', xOffset, contentY, imgWidth, imgHeight);
    }

    pdf.save(fileName);
    onSuccess?.();
  } catch (error) {
    console.error('Error generating PDF:', error);
    onError?.('Error al generar el PDF');
  }
}

export const getDaysFromDateRange = (startDateStr: string, endDateStr: string): string[] => {
  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);
  const days: string[] = [];

  let currentDate = startDate;
  while (currentDate.isSameOrBefore(endDate)) {
    days.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }

  return days;
};
